import { Controller, useFormContext, UseControllerProps } from 'react-hook-form';
import React, { useState } from 'react';
import { useWizardPanelContext } from '../components/Wizard.tsx';
import { CalendarIcon } from '@schuettflix/planum-icons-react';
import { useTranslation } from 'react-i18next';
import { PanelInputButton } from './PanelInputButton.tsx';
import {
    DispositionConfigurationPanel,
    DispositionConfigurationPanelProps,
    DispositionFormDataSchema,
    timeframeLabelLocaleKeys,
} from '@/modules/disposition/DispositionConfigurationPanel.tsx';

export type DispositionInputProps = Pick<UseControllerProps, 'name' | 'defaultValue' | 'disabled'> & {
    className?: string;
    quantity: number;
} & Pick<DispositionConfigurationPanelProps, 'loadingLocationData' | 'unloadingLocationData' | 'businessCase'>;

export const DispositionInput: React.FC<DispositionInputProps> = React.forwardRef<
    HTMLInputElement,
    DispositionInputProps
>(
    (
        { className, name, defaultValue, loadingLocationData, unloadingLocationData, businessCase, quantity, disabled },
        _ref
    ) => {
        const { control } = useFormContext();
        const [showDispositionConfig, setShowDispositionConfig] = useState(false);
        const { hasOpenedPanel } = useWizardPanelContext();
        const { t } = useTranslation();

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                disabled={hasOpenedPanel || disabled}
                render={({ field: { ref, onChange, value: _value, disabled }, fieldState }) => {
                    const value = _value ? DispositionFormDataSchema.parse(_value) : undefined;
                    const hasDateRange =
                        value?.dispositionDateRange &&
                        value.dispositionDateRange.start !== value.dispositionDateRange.end;
                    const hasCustomTimeframe = value?.dispositionTimeframe === 'CUSTOM';
                    return (
                        <>
                            <PanelInputButton
                                ref={ref}
                                leadingSlot={<CalendarIcon monocrome />}
                                isHighlighted={showDispositionConfig}
                                className={className}
                                label={t('forms.inputs.dispositionInput.planTransports')}
                                isInvalid={fieldState.invalid}
                                errorMessage={fieldState.error?.message}
                                value={
                                    value && (
                                        <span className="flex flex-col gap-2">
                                            <span className="font-copy-md-strong group-data-[highlighted]:text-dark block">
                                                {quantity === 1
                                                    ? t('forms.inputs.dispositionInput.transportsPlanned.one', {
                                                          quantity,
                                                      })
                                                    : t('forms.inputs.dispositionInput.transportsPlanned.other', {
                                                          quantity: quantity ?? 0,
                                                      })}
                                            </span>
                                            <span className="flex items-center gap-2">
                                                <span className="block">
                                                    {t('forms.inputs.dispositionInput.date')}:{' '}
                                                    {hasDateRange
                                                        ? `${value.dispositionDateRange.start} - ${value.dispositionDateRange.end}`
                                                        : value.dispositionDateRange.start}
                                                </span>
                                                <span className="bg-light-gray-300 block h-1 w-1 rounded-full" />
                                                <span className="block">
                                                    {t('forms.inputs.dispositionInput.timeframe')}:{' '}
                                                    {`${hasCustomTimeframe ? t('forms.inputs.dispositionInput.timeframeTime', { start: value.customDispositionStartTime, end: value.customDispositionEndTime }) : t(timeframeLabelLocaleKeys[value.dispositionTimeframe])}`}
                                                </span>
                                            </span>
                                        </span>
                                    )
                                }
                                onClick={() => {
                                    setShowDispositionConfig(true);
                                }}
                                data-test="dispatching-info-button"
                                disabled={disabled}
                            />
                            {showDispositionConfig && (
                                <DispositionConfigurationPanel
                                    defaultValues={value}
                                    businessCase={businessCase}
                                    loadingLocationData={loadingLocationData}
                                    unloadingLocationData={unloadingLocationData}
                                    onClose={() => setShowDispositionConfig(false)}
                                    onSubmit={dispositionFormData => {
                                        onChange(dispositionFormData);
                                        setShowDispositionConfig(false);
                                    }}
                                />
                            )}
                        </>
                    );
                }}
            />
        );
    }
);
